<script>
  import { mapActions, mapGetters } from 'vuex';
  import { required } from 'vuelidate/lib/validators';
  import InputControl from '@/_legacy/components/Form/InputControl.vue';
  import AutosizeTextarea from '@/_legacy/components/Form/AutosizeTextarea.vue';
  import CheckboxGroup from '@/_legacy/components/CheckboxGroup.vue';
  import InputControlDate from '@/_legacy/components/Form/InputControlDate.vue';
  import { INSPECTION_STATUS, ISSUE_PERMISSIONS, ISSUE_PRIORITY } from '@/_legacy/constants';
  import InputError from '@/_legacy/components/Form/InputError.vue';
  import CloseButton from '@/_legacy/components/Modals/CloseButton.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import AddPhotos from '@/_legacy/components/Issues/AddPhotos.vue';
  import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { findIssueInInspectionByQuestion } from '@/ui/components/issue/helpers';
  import encoders from '@/_legacy/store/encoders';
  import { ChooseUserField } from '@/ui/components/user/ChooseUserField';
  import { DateTimeField } from '@/ui/components/form';
  import { useTimeZonedDates } from '@/ui/concerns/useTimeZonedDates';
  import { toUnixTimestamp, fromUnixTimestamp } from '@/_legacy/util/UnixTimestamps';
  import Approvals from '@/_legacy/views/ChecklistBuilder/Settings/Approvals.vue';
  import { approvalsEnabledForOrganization, hasSomePermission, Permissions } from '@/ui/auth/auth';
  import { filterListToOptionsUsers, unixTimestampToDate } from '@/ui/helpers';
  import { useTask } from '@/ui/concerns';
  import { filter as userFilter } from '@/services/api/resources/user';
  import { kebabCase } from 'lodash';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';

  export default {
    name: 'RaiseIssueModal',

    components: {
      Approvals,
      SelectControl,
      CloseButton,
      InputError,
      InputControlDate,
      InputControl,
      AutosizeTextarea,
      CheckboxGroup,
      AddPhotos,
      PrioritySelector,
      ChooseUserField,
      DateTimeField,
    },

    mixins: [authMixin],

    props: {
      questionId: {
        type: String,
        required: true,
      },
    },

    data: () => ({
      isIssueExists: false,
      form: {
        id: null,
        title: '',
        description: '',
        assignees: [],
        priority: ISSUE_PRIORITY.MEDIUM,
        deadline: null,
        suggestedCorrectiveAction: '',
        photos: [],
        approvalsEnabled: false,
        approvalsEntitySpecificAssignments: false,
        approvers: [],
      },
      userActionApprovalOptions: [],
    }),

    validations: {
      form: {
        title: { required },
      },
    },

    computed: {
      CreateUserModelIdentifier() {
        return CreateUserModelIdentifier;
      },
      ...mapGetters({
        inspection: 'conduct/inspection',
        assignees: 'assignees',
      }),

      canCreateIssueWithAssignee() {
        return this.hasPermission(
          ISSUE_PERMISSIONS.CREATE_ISSUE_WITH_ASSIGNEE,
        );
      },

      issuesAsArray() {
        return Array.isArray(this.inspection.issues)
          ? this.inspection.issues
          : Object.values(this.inspection.issues);
      },

      issueQuestionId() {
        if (!this.inspection.issues) {
          return false;
        }

        if (this.inspection.issues[this.questionId]) {
          return true;
        }

        return this.inspection.status === INSPECTION_STATUS.DRAFT
          && findIssueInInspectionByQuestion(this.issuesAsArray, this.questionId);
      },

      isDraft() {
        return this.inspection.status === INSPECTION_STATUS.DRAFT;
      },

      isDone() {
        return this.inspection.status === INSPECTION_STATUS.DONE && this.inspection.todoId;
      },

      issueForInspection() {
        if (!this.inspection.issues || this.isDone) {
          return null;
        }

        const issue = this.inspection.issues[this.questionId];

        return this.isDraft && issue?.id
          ? this.normalizeIssueFromInspectionToForm(issue)
          : issue;
      },

      assigneesOptions() {
        return this.assignees;
      },

      approveAssigneesOptions() {
        return this.userActionApprovalOptions;
      },

      timeZonedDeadline() {
        return this.form.deadline
          ? useTimeZonedDates().getDateInUserTimeZone(unixTimestampToDate(this.form.deadline))
          : this.form.deadline;
      },
    },

    mounted() {
      this.getActionApprovalUsers().run();

      if (!this.issueForInspection) {
        return;
      }

      const {
        id,
        title,
        description,
        priority,
        deadline,
        // eslint-disable-next-line camelcase
        suggested_corrective_action,
        assignees,
        photos,
      } = this.issueForInspection.params;

      this.form = {
        id,
        title,
        description,
        assignees: assignees?.users || [],
        priority,
        deadline,
        suggestedCorrectiveAction: suggested_corrective_action,
        photos,
        approvalsEnabled: this.form.approvalsEnabled,
        approvalsEntitySpecificAssignments: this.form.approvalsEntitySpecificAssignments,
        approvers: this.form.approvers?.users || [],
      };
    },

    methods: {
      ...mapActions({
        issueSaved: 'conduct/issueSaved',
        issueRemoved: 'conduct/issueRemoved',
      }),

      handleClose() {
        this.$emit('close');
      },

      getActionApprovalUsers() {
        return useTask(
          () => userFilter({
            permission: [Permissions.APPROVE_ALL_ACTIONS, Permissions.APPROVE_ASSIGNED_ACTIONS].map(kebabCase),
          }),
          response => {
            this.userActionApprovalOptions = [
              ...filterListToOptionsUsers(response.data),
            ];
          },
        );
      },

      canSeeApprovals() {
        return approvalsEnabledForOrganization.value
          && hasSomePermission([Permissions.APPROVE_ALL_ACTIONS, Permissions.APPROVE_ASSIGNED_INSPECTIONS]);
      },

      saveButtonClicked() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          this.$toasted.show(
            this.$t('This page has form errors, please fix them'),
            { type: 'error' },
          );
        } else {
          this.issueSaved({
            questionId: this.questionId,
            form: this.form,
          });
          this.handleClose();
        }
      },

      removeButtonClicked() {
        this.issueRemoved(this.questionId);
        this.handleClose();
      },

      normalizeIssueFromInspectionToForm(issue) {
        return encoders.actionIssue({
          ...issue,
          deadline: issue.due_at ? issue.due_at : null,
          assignees: Object.keys(issue.params.users).map(id => parseInt(id))
        });
      },

      assigneesChanged(assignees) {
        this.form.assignees = assignees;
      },

      approversChange(approvers) {
        this.form.approvers = approvers;
      },

      deadlineChanged(deadline) {
        if (!deadline) {
          this.form.deadline = null;

          return;
        }

        const newDeadline = useTimeZonedDates().getDateInLocalTimeZone(deadline);
        this.form.deadline = toUnixTimestamp(newDeadline.getTime());
      },
    },
  };
</script>

<template>
  <modal class="overflow-scroll">
    <div class="modal-wrapper modal-wrapper-action">
      <CloseButton @click.native="handleClose" />

      <div
        class="modal-content modal-content--scrollable"
        :class="{ 'modal-content--editting': issueQuestionId }"
      >
        <InputControl :label="$t('Title')">
          <input
            id="title"
            v-model.trim.lazy="$v.form.title.$model"
            class="form-input form-control form-input-bordered w-full"
            name="title"
            type="text"
            :placeholder="$t('Enter title')"
            maxlength="255"
          >
          <div v-if="$v.form.title.$anyError">
            <InputError
              v-if="$v.form.title.$dirty"
              :condition="!$v.form.title.required"
            >
              {{
                $t('The {field} field is required', {
                  field: $t('title'),
                })
              }}
            </InputError>
          </div>
        </InputControl>

        <InputControl :label="$t('Description (optional)')">
          <AutosizeTextarea
            v-model.trim.lazy="form.description"
            :placeholder="$t('Enter description')"
          />
        </InputControl>

        <InputControl>
          <AddPhotos v-model="form.photos" />
        </InputControl>

        <InputControl
          v-if="canCreateIssueWithAssignee"
          :label="$t('Assignee (optional)')"
        >
          <ChooseUserField
            :options="assigneesOptions"
            :value="form.assignees"
            :hide-label="true"
            :placeholder="$t('Select assignees')"
            hide-create-user="true"
            @input="assigneesChanged"
          />
        </InputControl>

        <InputControl :label="$t('Priority')">
          <PrioritySelector
            v-model="form.priority"
          />
        </InputControl>

        <InputControl :label="$t('common.deadline')">
          <DateTimeField
            name="deadline"
            :value="timeZonedDeadline"
            :hide-label="true"
            :placeholder="$t('Select deadline')"
            :required="false"
            position="is-top-right"
            :locale="loggedInUser().locale"
            :clearable="true"
            @input="deadlineChanged"
          />
        </InputControl>

        <Approvals v-if="canSeeApprovals"
            :checklist-approvers="{users: form.approvers}"
            :users="approveAssigneesOptions"
            :issue="true"
            :approvals-enabled.sync="form.approvalsEnabled"
            :entity-restrictions.sync="form.approvalsEntitySpecificAssignments"
            :identifier="CreateUserModelIdentifier.issueBuilderApprovers"
             @input="approversChange"
            @update:checklistApprovers="form.approvers = $event.users"
          />

        <p class="mb-4">
          {{ $t('Click done to raise issue when submitting your check results') }}
        </p>
      </div>

      <div class="modal-actions-outer-wrapper">
        <div class="modal-actions-inner-wrapper">
          <button
            v-if="issueForInspection"
            class="modal-actions-secondary modal-actions-secondary--transparent"
            type="button"
            @click="removeButtonClicked"
          >
            <span>{{ $t('Remove') }}</span>
          </button>
          <button
            class="modal-actions-primary"
            type="button"
            @click="saveButtonClicked"
          >
            <span>{{ $t('Done') }}</span>
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>
